import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';

interface SummaryData {
  gross_booking_value: number;
  platform_revenue: number;
  provider_payouts: number;
  jobs_completed: number;
  after_hours_revenue: number;
  late_callout_revenue: number;
}

interface CityData {
  city: string;
  tier: string;
  jobs: number;
  gross: number;
  commission: number;
  provider_payout: number;
}

interface ServiceData {
  service: string;
  jobs: number;
  gross: number;
  commission: number;
  avg_job: number;
}

interface JobData {
  job_id: string;
  date: string;
  city: string;
  tier: string;
  service: string;
  provider_name: string;
  provider_id: string;
  duration_minutes: number;
  customer_paid: number;
  commission: number;
  provider_payout: number;
  after_hours: boolean;
  late_callout: boolean;
  status: string;
}

interface JobDetailData {
  job_id: string;
  client_name: string;
  provider_name: string;
  city: string;
  tier: string;
  service: string;
  date: string;
  completed_at: string;
  duration_minutes: number;
  line_items: Array<{
    code: string;
    label: string;
    amount: number;
  }>;
  subtotal: number;
  commission_rate: number;
  commission: number;
  provider_payout: number;
  refund_amount: number | null;
  status: string;
}

export default function AdminEarningsPage() {
  const [timeframe, setTimeframe] = useState<'today' | '7days' | '30days' | 'custom'>('30days');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [cityData, setCityData] = useState<CityData[]>([]);
  const [serviceData, setServiceData] = useState<ServiceData[]>([]);
  const [jobs, setJobs] = useState<JobData[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    city: '',
    tier: '',
    service: '',
    after_hours: '',
    late_callout: '',
  });

  const getDateRange = () => {
    const now = new Date();
    let from = '';
    let to = now.toISOString();

    if (timeframe === 'today') {
      from = new Date(now.setHours(0, 0, 0, 0)).toISOString();
    } else if (timeframe === '7days') {
      from = new Date(now.setDate(now.getDate() - 7)).toISOString();
    } else if (timeframe === '30days') {
      from = new Date(now.setDate(now.getDate() - 30)).toISOString();
    } else if (timeframe === 'custom' && customFrom && customTo) {
      from = new Date(customFrom).toISOString();
      to = new Date(customTo).toISOString();
    }

    return { from, to };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { from, to } = getDateRange();
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      // Fetch summary
      const summaryRes = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-admin-earnings-summary?${params}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      const summaryData = await summaryRes.json();
      setSummary(summaryData);

      // Fetch city data
      const cityRes = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-admin-earnings-by-city?${params}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      const cityDataRes = await cityRes.json();
      setCityData(cityDataRes);

      // Fetch service data
      const serviceRes = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-admin-earnings-by-service?${params}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      const serviceDataRes = await serviceRes.json();
      setServiceData(serviceDataRes);

      // Fetch jobs
      await fetchJobs(from, to, 1);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async (from: string, to: string, page: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      params.append('page', page.toString());
      if (filters.city) params.append('city', filters.city);
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.service) params.append('service', filters.service);
      if (filters.after_hours) params.append('after_hours', filters.after_hours);
      if (filters.late_callout) params.append('late_callout', filters.late_callout);

      const jobsRes = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-admin-earnings-jobs?${params}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      const jobsData = await jobsRes.json();
      setJobs(jobsData.jobs || []);
      setTotalPages(jobsData.total_pages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchJobDetail = async (jobId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-admin-earnings-job-detail?job_id=${jobId}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      const data = await res.json();
      setSelectedJob(data);
    } catch (error) {
      console.error('Error fetching job detail:', error);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Job ID',
      'Date',
      'City',
      'Tier',
      'Service',
      'Provider',
      'Duration (min)',
      'Customer Paid',
      'Commission',
      'Provider Payout',
      'After-Hours',
      'Late Call-Out',
      'Status',
    ];

    const rows = jobs.map((job) => [
      job.job_id,
      new Date(job.date).toLocaleDateString(),
      job.city,
      job.tier,
      job.service,
      job.provider_name,
      job.duration_minutes,
      `£${job.customer_paid.toFixed(2)}`,
      `£${job.commission.toFixed(2)}`,
      `£${job.provider_payout.toFixed(2)}`,
      job.after_hours ? 'Yes' : 'No',
      job.late_callout ? 'Yes' : 'No',
      job.status,
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `earnings-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  useEffect(() => {
    fetchData();
  }, [timeframe, customFrom, customTo]);

  useEffect(() => {
    const { from, to } = getDateRange();
    fetchJobs(from, to, 1);
  }, [filters]);

  const daytimeJobs = jobs.filter((j) => !j.after_hours);
  const afterHoursJobs = jobs.filter((j) => j.after_hours);
  const daytimeGross = daytimeJobs.reduce((sum, j) => sum + j.customer_paid, 0);
  const afterHoursGross = afterHoursJobs.reduce((sum, j) => sum + j.customer_paid, 0);
  const daytimeCommission = daytimeJobs.reduce((sum, j) => sum + j.commission, 0);
  const afterHoursCommission = afterHoursJobs.reduce((sum, j) => sum + j.commission, 0);

  const lateCalloutJobs = jobs.filter((j) => j.late_callout);
  const totalCalloutFees = lateCalloutJobs.length * 50;
  const calloutPlatformShare = totalCalloutFees * 0.15;
  const calloutProviderShare = totalCalloutFees * 0.85;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading earnings data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Earnings Reports</h1>
          <p className="text-gray-600 mt-2">Platform financial overview and performance metrics</p>
        </div>

        {/* Timeframe Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => setTimeframe('today')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                timeframe === 'today'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setTimeframe('7days')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                timeframe === '7days'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 7 days
            </button>
            <button
              onClick={() => setTimeframe('30days')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                timeframe === '30days'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 30 days
            </button>
            <button
              onClick={() => setTimeframe('custom')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                timeframe === 'custom'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Custom range
            </button>
            {timeframe === 'custom' && (
              <>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                />
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                />
              </>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Total booking value</h3>
                <i className="ri-money-pound-circle-line text-2xl text-blue-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900">£{summary.gross_booking_value.toFixed(2)}</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Platform revenue</h3>
                <i className="ri-line-chart-line text-2xl text-teal-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900">£{summary.platform_revenue.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">15% commission earned on completed jobs</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Provider payouts</h3>
                <i className="ri-wallet-3-line text-2xl text-green-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900">£{summary.provider_payouts.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Total earnings paid to providers</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Jobs completed</h3>
                <i className="ri-checkbox-circle-line text-2xl text-purple-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900">{summary.jobs_completed}</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">After-hours revenue</h3>
                <i className="ri-moon-line text-2xl text-indigo-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900">£{summary.after_hours_revenue.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Commission earned from jobs between 6pm and 6am</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Late call-out revenue</h3>
                <i className="ri-alarm-warning-line text-2xl text-orange-600"></i>
              </div>
              <p className="text-3xl font-bold text-gray-900">£{summary.late_callout_revenue.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">Commission earned from short-notice after-hours fees</p>
            </div>
          </div>
        )}

        {/* By City/Tier */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">By City / Tier</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">City</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tier</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Jobs</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Gross (£)</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Commission (£)</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Provider Payout (£)</th>
                </tr>
              </thead>
              <tbody>
                {cityData.map((city, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">{city.city}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{city.tier}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">{city.jobs}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">£{city.gross.toFixed(2)}</td>
                    <td className="py-3 px-4 text-sm text-teal-600 text-right">£{city.commission.toFixed(2)}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">£{city.provider_payout.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* By Service Type */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">By Service Type</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Service</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Jobs</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Gross (£)</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Commission (£)</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Avg Job (£)</th>
                </tr>
              </thead>
              <tbody>
                {serviceData.map((service, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900 capitalize">{service.service}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">{service.jobs}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">£{service.gross.toFixed(2)}</td>
                    <td className="py-3 px-4 text-sm text-teal-600 text-right">£{service.commission.toFixed(2)}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">£{service.avg_job.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Day vs After-Hours */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Day vs After-Hours</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Jobs</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Gross (£)</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Commission (£)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-900">Daytime</td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-right">{daytimeJobs.length}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-right">£{daytimeGross.toFixed(2)}</td>
                  <td className="py-3 px-4 text-sm text-teal-600 text-right">£{daytimeCommission.toFixed(2)}</td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-900">After-hours</td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-right">{afterHoursJobs.length}</td>
                  <td className="py-3 px-4 text-sm text-gray-900 text-right">£{afterHoursGross.toFixed(2)}</td>
                  <td className="py-3 px-4 text-sm text-teal-600 text-right">£{afterHoursCommission.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Late Call-Out Fees */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Late Call-Out Fees</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Jobs with late call-out</p>
              <p className="text-2xl font-bold text-gray-900">{lateCalloutJobs.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Total call-out fees (£)</p>
              <p className="text-2xl font-bold text-gray-900">£{totalCalloutFees.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Platform share (15%)</p>
              <p className="text-2xl font-bold text-teal-600">£{calloutPlatformShare.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Provider share (85%)</p>
              <p className="text-2xl font-bold text-green-600">£{calloutProviderShare.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Jobs Table */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Detailed Jobs</h2>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-download-line"></i>
              Export CSV
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <select
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Cities</option>
              {Array.from(new Set(cityData.map((c) => c.city))).map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>

            <select
              value={filters.service}
              onChange={(e) => setFilters({ ...filters, service: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Services</option>
              <option value="handyman">Handyman</option>
              <option value="plumber">Plumber</option>
              <option value="electrician">Electrician</option>
              <option value="cleaner">Cleaner</option>
            </select>

            <select
              value={filters.tier}
              onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Tiers</option>
              <option value="TIER_1_LONDON">Tier 1 - London</option>
              <option value="TIER_2_MAJOR">Tier 2 - Major Cities</option>
              <option value="TIER_3_WALES">Tier 3 - Wales</option>
            </select>

            <select
              value={filters.after_hours}
              onChange={(e) => setFilters({ ...filters, after_hours: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Times</option>
              <option value="true">After-hours only</option>
            </select>

            <select
              value={filters.late_callout}
              onChange={(e) => setFilters({ ...filters, late_callout: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Jobs</option>
              <option value="true">Late call-out only</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Job ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">City</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Service</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Provider</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Duration</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Customer Paid</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Commission</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Provider Payout</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">After-Hours</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Late Call-Out</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.job_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">{job.job_id.slice(0, 8)}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(job.date).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900">{job.city}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 capitalize">{job.service}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{job.provider_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">{job.duration_minutes}m</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">£{job.customer_paid.toFixed(2)}</td>
                    <td className="py-3 px-4 text-sm text-teal-600 text-right">£{job.commission.toFixed(2)}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">£{job.provider_payout.toFixed(2)}</td>
                    <td className="py-3 px-4 text-center">
                      {job.after_hours ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          Yes
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">No</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {job.late_callout ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Yes
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">No</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => fetchJobDetail(job.job_id)}
                        className="text-teal-600 hover:text-teal-700 font-medium text-sm whitespace-nowrap"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => {
                  const { from, to } = getDateRange();
                  fetchJobs(from, to, currentPage - 1);
                }}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => {
                  const { from, to } = getDateRange();
                  fetchJobs(from, to, currentPage + 1);
                }}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Job Details</h3>
              <button
                onClick={() => setSelectedJob(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              {/* Job Summary */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Job Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Job ID</p>
                    <p className="text-sm font-medium text-gray-900">{selectedJob.job_id.slice(0, 8)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">City</p>
                    <p className="text-sm font-medium text-gray-900">{selectedJob.city}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Service</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">{selectedJob.service}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Tier</p>
                    <p className="text-sm font-medium text-gray-900">{selectedJob.tier}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Client</p>
                    <p className="text-sm font-medium text-gray-900">{selectedJob.client_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Provider</p>
                    <p className="text-sm font-medium text-gray-900">{selectedJob.provider_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date & Time</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(selectedJob.date).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="text-sm font-medium text-gray-900">
                      {Math.floor(selectedJob.duration_minutes / 60)}h {selectedJob.duration_minutes % 60}m
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Pricing Breakdown</h4>
                <div className="space-y-3">
                  {selectedJob.line_items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-700">{item.label}</span>
                      <span className="text-sm font-medium text-gray-900">£{item.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between py-3 border-t-2 border-gray-200">
                    <span className="text-base font-semibold text-gray-900">Subtotal</span>
                    <span className="text-base font-bold text-gray-900">£{selectedJob.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-gray-700">Taskbudy fee (15%)</span>
                    <span className="text-sm font-medium text-teal-600">−£{selectedJob.commission.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-t-2 border-gray-200">
                    <span className="text-base font-semibold text-gray-900">Provider earned</span>
                    <span className="text-base font-bold text-green-600">£{selectedJob.provider_payout.toFixed(2)}</span>
                  </div>
                  {selectedJob.refund_amount && (
                    <div className="flex items-center justify-between py-2 bg-red-50 px-3 rounded-lg">
                      <span className="text-sm font-medium text-red-700">Refund Amount</span>
                      <span className="text-sm font-bold text-red-700">−£{selectedJob.refund_amount.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-600">
                  Platform fee covers payment processing, customer support, scheduling, and job protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
